/**
 * UI-only store for simulation local-draft save feedback.
 * Shows “Saving…” / “Saved” only when `useSimulationDraft`’s `requestSaveIndicator`
 * schedules a debounced write (module-specific), not on silent timer persists or flush.
 *
 * @module useSimulationDraftPersistStore
 */

import { create } from 'zustand';

/** Delay before showing "Saving…" so fast flushes never flash the label */
const SAVING_REVEAL_MS = 160;
/** How long to show "Saved" before hiding */
const SAVED_VISIBLE_MS = 2400;

export type SimulationDraftPersistPhase = 'idle' | 'saving' | 'saved';

let savingRevealTimer: ReturnType<typeof setTimeout> | null = null;
let savedHideTimer: ReturnType<typeof setTimeout> | null = null;

function clearSavingRevealTimer() {
  if (savingRevealTimer) {
    clearTimeout(savingRevealTimer);
    savingRevealTimer = null;
  }
}

function clearSavedHideTimer() {
  if (savedHideTimer) {
    clearTimeout(savedHideTimer);
    savedHideTimer = null;
  }
}

interface SimulationDraftPersistStore {
  readonly phase: SimulationDraftPersistPhase;
  /** Called when a debounced write is scheduled (payload changed). */
  readonly scheduleDebouncedWrite: () => void;
  /** Called immediately after {@link writeDraft} returns (debounced or flush). */
  readonly notifyWriteFinished: (success: boolean) => void;
  /** Clear timers and hide indicator (e.g. leaving module step). */
  readonly reset: () => void;
}

export const useSimulationDraftPersistStore = create<SimulationDraftPersistStore>((set) => ({
  phase: 'idle',

  scheduleDebouncedWrite: () => {
    clearSavedHideTimer();
    clearSavingRevealTimer();

    savingRevealTimer = setTimeout(() => {
      savingRevealTimer = null;
      set({ phase: 'saving' });
    }, SAVING_REVEAL_MS);
  },

  notifyWriteFinished: (success: boolean) => {
    clearSavingRevealTimer();

    if (!success) {
      clearSavedHideTimer();
      set({ phase: 'idle' });
      return;
    }

    clearSavedHideTimer();
    set({ phase: 'saved' });

    savedHideTimer = setTimeout(() => {
      savedHideTimer = null;
      set({ phase: 'idle' });
    }, SAVED_VISIBLE_MS);
  },

  reset: () => {
    clearSavingRevealTimer();
    clearSavedHideTimer();
    set({ phase: 'idle' });
  },
}));
